import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Mail, MessageSquare, TrendingUp, Download, Trash2, LogOut, Pizza, RefreshCw, CheckCircle, Clock } from 'lucide-react';
import { downloadCSV } from '../utils/exportUtils';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = sessionStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminDashboard({ onLogout }) {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [contactsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
      ]);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        onLogout();
        return;
      }
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: getAuthHeaders() });
      setContacts(prev => prev.filter(c => c.id !== id));
      fetchData(); // refresh stats
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const handleExport = () => {
    const headers = ['Name', 'Email', 'Phone', 'Restaurant', 'Email Opt-In', 'SMS Opt-In', 'Follow-Up Sent', 'Date'];
    const rows = contacts.map(c => [
      c.name, c.email, c.phone, c.restaurantName,
      c.emailOptIn ? 'Yes' : 'No', c.smsOptIn ? 'Yes' : 'No',
      c.followUpSent ? 'Yes' : 'No',
      c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '',
    ]);
    downloadCSV('pizzalator-leads', headers, rows);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    onLogout();
  };

  return (
    <div className="min-h-screen bg-[#FFF8E7]">
      {/* Header */}
      <header className="bg-[#2A2A2A] text-white border-b-2 border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#E23D28] rounded-full flex items-center justify-center">
              <Pizza className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg leading-none" style={{ fontFamily: "'Bebas Neue', cursive" }}>Admin Console</h1>
              <p className="text-xs text-white/50">Mikey Mozzarella's Pizza-lator</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchData} className="text-white/60 hover:text-white" data-testid="admin-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={handleLogout} className="flex items-center gap-1 text-sm text-white/60 hover:text-white" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {loading ? (
          <div className="text-center py-16">
            <RefreshCw className="w-8 h-8 text-[#2A2A2A]/20 mx-auto mb-2 animate-spin" />
            <p className="text-sm text-[#2A2A2A]/40">Loading...</p>
          </div>
        ) : error ? (
          <div className="pizza-card p-6 text-center">
            <p className="text-[#E23D28]">{error}</p>
            <button onClick={fetchData} className="pizza-btn text-sm mt-3">Retry</button>
          </div>
        ) : (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8" data-testid="admin-stats">
                <div className="stat-card text-center">
                  <Users className="w-5 h-5 text-[#E23D28] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{stats.totalContacts}</p>
                  <p className="text-xs text-[#2A2A2A]/50">Total Leads</p>
                </div>
                <div className="stat-card text-center">
                  <TrendingUp className="w-5 h-5 text-[#2E7D32] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#2E7D32]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{stats.todayLeads}</p>
                  <p className="text-xs text-[#2A2A2A]/50">Today</p>
                </div>
                <div className="stat-card text-center">
                  <Mail className="w-5 h-5 text-[#FFC107] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{stats.emailOptIns}</p>
                  <p className="text-xs text-[#2A2A2A]/50">Email Opt-ins</p>
                </div>
                <div className="stat-card text-center">
                  <MessageSquare className="w-5 h-5 text-[#E23D28] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{stats.smsOptIns}</p>
                  <p className="text-xs text-[#2A2A2A]/50">SMS Opt-ins</p>
                </div>
                <div className="stat-card text-center">
                  <CheckCircle className="w-5 h-5 text-[#2E7D32] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{stats.followUpsSent}</p>
                  <p className="text-xs text-[#2A2A2A]/50">Emails Sent</p>
                </div>
                <div className="stat-card text-center">
                  <p className="text-2xl font-bold text-[#FFC107]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{stats.emailOptInRate}%</p>
                  <p className="text-xs text-[#2A2A2A]/50">Email Rate</p>
                </div>
                <div className="stat-card text-center">
                  <p className="text-2xl font-bold text-[#FFC107]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{stats.smsOptInRate}%</p>
                  <p className="text-xs text-[#2A2A2A]/50">SMS Rate</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                Contacts / Leads ({contacts.length})
              </h2>
              <button onClick={handleExport} className="pizza-btn-secondary text-xs px-4 py-2 flex items-center gap-1" data-testid="admin-export-btn"
                disabled={contacts.length === 0}>
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>

            {/* Contacts table */}
            {contacts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="pizza-table" data-testid="admin-contacts-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Restaurant</th>
                      <th>Email Opt-In</th>
                      <th>SMS Opt-In</th>
                      <th>Follow-Up</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map(c => (
                      <tr key={c.id}>
                        <td className="font-medium text-[#2A2A2A]">{c.name}</td>
                        <td className="text-sm">{c.email}</td>
                        <td className="text-sm">{c.phone}</td>
                        <td className="text-sm text-[#2A2A2A]/60">{c.restaurantName || '-'}</td>
                        <td>{c.emailOptIn ? <span className="pizza-tag pizza-tag-green text-xs">Yes</span> : <span className="text-xs text-[#2A2A2A]/30">No</span>}</td>
                        <td>{c.smsOptIn ? <span className="pizza-tag pizza-tag-green text-xs">Yes</span> : <span className="text-xs text-[#2A2A2A]/30">No</span>}</td>
                        <td>
                          {c.followUpSent ? (
                            <span className="flex items-center gap-1 text-xs text-[#2E7D32]"><CheckCircle className="w-3 h-3" /> Sent</span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-[#FFC107]"><Clock className="w-3 h-3" /> Pending</span>
                          )}
                        </td>
                        <td className="text-xs text-[#2A2A2A]/50">{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '-'}</td>
                        <td>
                          <button onClick={() => handleDelete(c.id)} className="p-1 hover:bg-red-100 rounded" data-testid={`admin-delete-${c.id}`}>
                            <Trash2 className="w-4 h-4 text-[#E23D28]" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="pizza-card p-8 text-center" data-testid="admin-no-contacts">
                <Users className="w-12 h-12 text-[#2A2A2A]/20 mx-auto mb-3" />
                <h3 className="text-xl text-[#2A2A2A] mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>No Leads Yet</h3>
                <p className="text-sm text-[#2A2A2A]/50">Contacts will appear here as users download their reports.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
